import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, AlertCircle, ExternalLink, Image as ImageIcon, ListMusic, Headphones, Download, Tag, Calendar, FileText } from "lucide-react";
import api from "@/lib/upr-api";

const FILTERS = [
  { key: "incomplete", label: "Needs work" },
  { key: "complete", label: "Complete" },
  { key: "all", label: "All" },
];

const FIELD_META = [
  { key: "has_cover", label: "Cover", icon: ImageIcon },
  { key: "has_tracks", label: "Tracks", icon: ListMusic },
  { key: "has_previews", label: "Previews (MP3)", icon: Headphones },
  { key: "has_downloads", label: "Downloads (MP3)", icon: Download },
  { key: "has_tags", label: "Tags", icon: Tag },
  { key: "has_release_date", label: "Release date", icon: Calendar },
  { key: "has_description", label: "Description", icon: FileText },
];

export default function AuditAdmin() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("incomplete");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/audit/catalog")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "incomplete") return data.items.filter((i) => !i.complete);
    if (filter === "complete") return data.items.filter((i) => i.complete);
    return data.items;
  }, [data, filter]);

  if (loading) return <div className="text-sm" style={{ color: "var(--upr-muted)" }}>Auditing the catalog…</div>;
  if (!data) return <div className="text-sm" style={{ color: "var(--upr-muted)" }}>Could not load audit data.</div>;

  const s = data.summary;

  return (
    <section data-testid="audit-admin">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-heading font-extrabold text-2xl tracking-tight">Catalog Audit</h2>
        <div className="upr-eyebrow" data-testid="audit-completion">
          {s.complete}/{s.total} complete · {s.incomplete} needs work
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-8">
        {FIELD_META.map((f) => {
          const missingKey = "missing_" + f.key.replace("has_", "");
          const missing = s[missingKey] ?? 0;
          const Icon = f.icon;
          const ok = missing === 0;
          return (
            <div
              key={f.key}
              className="p-3"
              style={{ border: "1px solid var(--upr-border)", background: ok ? "transparent" : "var(--upr-surface)" }}
              data-testid={`audit-tile-${f.key}`}
            >
              <div className="flex items-center gap-1.5">
                <Icon size={13} style={{ color: ok ? "var(--upr-secondary)" : "var(--upr-primary)" }} />
                <span className="upr-eyebrow" style={{ fontSize: "0.6rem" }}>{f.label}</span>
              </div>
              <div className="font-heading font-extrabold text-2xl mt-1" style={{ color: ok ? "var(--upr-secondary)" : "var(--upr-primary)" }}>
                {ok ? "✓" : missing}
              </div>
              <div className="text-[0.62rem]" style={{ color: "var(--upr-muted)" }}>
                {ok ? "all set" : "missing"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="audit-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`audit-filter-${f.key}`}
              className="font-heading uppercase tracking-widest text-xs px-3 py-1.5 border transition-colors"
              style={{
                background: active ? "var(--upr-fg)" : "transparent",
                color: active ? "var(--upr-bg)" : "var(--upr-fg)",
                borderColor: active ? "var(--upr-fg)" : "var(--upr-border)",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Rows */}
      {filtered.length === 0 ? (
        <div className="text-sm py-12 text-center" style={{ color: "var(--upr-muted)" }} data-testid="audit-empty">
          {filter === "incomplete" ? "Everything looks shipshape ✓" : "No releases match this filter."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="audit-list">
          {filtered.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-4 p-3"
              style={{ border: "1px solid var(--upr-border)", background: r.complete ? "transparent" : "var(--upr-surface)" }}
              data-testid={`audit-row-${r.id}`}
            >
              <div className="w-12 h-12 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}>
                {r.cover_url ? <img src={r.cover_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><AlertCircle size={16} style={{ color: "var(--upr-primary)" }} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold truncate">
                  {r.title || "(untitled)"}
                  <span className="upr-eyebrow ml-2" style={{ color: "var(--upr-muted)" }}>{r.catalog_number}</span>
                </div>
                <div className="text-xs" style={{ color: "var(--upr-muted)" }}>{r.artist_name}</div>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1" data-testid={`audit-flags-${r.id}`}>
                  {FIELD_META.map((f) => {
                    const ok = r[f.key];
                    const Icon = ok ? Check : X;
                    return (
                      <span
                        key={f.key}
                        className="inline-flex items-center gap-1 text-[0.7rem]"
                        style={{ color: ok ? "var(--upr-secondary)" : "var(--upr-primary)" }}
                        data-testid={`audit-flag-${r.id}-${f.key}`}
                      >
                        <Icon size={11} />{f.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <Link
                to={`/admin?tab=releases&edit=${r.id}`}
                className="upr-btn upr-btn-ghost shrink-0"
                data-testid={`audit-row-${r.id}-fix`}
                title="Jump to Releases tab"
              >
                Fix <ExternalLink size={11} />
              </Link>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
